import React, { useState, useEffect } from 'react';
import { EnvironmentConfig } from '../config/environment';

const StatsLeaderboard = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('global');
  const [globalStats, setGlobalStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [playerStats, setPlayerStats] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const env = new EnvironmentConfig();
  const apiUrl = env.getApiUrl();

  const fetchGlobalStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${apiUrl}/api/stats/global`);
      const data = await response.json();
      
      if (data.success) {
        setGlobalStats(data.stats);
      } else {
        setError(data.error || 'Failed to fetch global statistics');
      }
    } catch (err) {
      setError('Network error while fetching global statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${apiUrl}/api/stats/leaderboard`);
      const data = await response.json();
      
      if (data.success) {
        setLeaderboard(data.leaderboard);
      } else {
        setError(data.error || 'Failed to fetch leaderboard');
      }
    } catch (err) {
      setError('Network error while fetching leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerStats = async (name) => {
    if (!name.trim()) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${apiUrl}/api/stats/player`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerName: name.trim() })
      });
      const data = await response.json();
      
      if (data.success) {
        setPlayerStats(data.stats);
      } else {
        setError(data.error || 'Player not found');
        setPlayerStats(null);
      }
    } catch (err) {
      setError('Network error while fetching player statistics');
      setPlayerStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'global') {
      fetchGlobalStats();
    } else if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    }
  }, [activeTab]);

  const formatDuration = (ms) => {
    if (!ms) return '0m';
    const minutes = Math.floor(ms / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getMostPopular = (obj) => {
    if (!obj || Object.keys(obj).length === 0) return 'N/A';
    const entries = Object.entries(obj);
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0][0];
  };

  const renderGlobalStats = () => {
    if (!globalStats) return <div>No data available</div>;

    return (
      <div className="stats-content">
        <div className="stats-grid">
          <div className="stat-card">
            <h3>📊 Game Statistics</h3>
            <div className="stat-row">
              <span>Total Games:</span>
              <span>{globalStats.totalGames?.toLocaleString() || 0}</span>
            </div>
            <div className="stat-row">
              <span>Total Moves:</span>
              <span>{globalStats.totalMoves?.toLocaleString() || 0}</span>
            </div>
            <div className="stat-row">
              <span>Average Game Duration:</span>
              <span>{formatDuration(globalStats.averageGameDuration)}</span>
            </div>
          </div>

          <div className="stat-card">
            <h3>🏆 Game Outcomes</h3>
            <div className="stat-row">
              <span>Games Won:</span>
              <span>{globalStats.gamesFinishedByWin || 0}</span>
            </div>
            <div className="stat-row">
              <span>Games Resigned:</span>
              <span>{globalStats.gamesFinishedByResign || 0}</span>
            </div>
            <div className="stat-row">
              <span>Win Rate:</span>
              <span>
                {globalStats.totalGames > 0 
                  ? `${Math.round((globalStats.gamesFinishedByWin / globalStats.totalGames) * 100)}%`
                  : '0%'
                }
              </span>
            </div>
          </div>

          <div className="stat-card">
            <h3>⚙️ Popular Settings</h3>
            <div className="stat-row">
              <span>Most Popular Board Size:</span>
              <span>{getMostPopular(globalStats.mostPopularBoardSize)}×{getMostPopular(globalStats.mostPopularBoardSize)}</span>
            </div>
            <div className="stat-row">
              <span>Most Popular Win Condition:</span>
              <span>{getMostPopular(globalStats.mostPopularWinCondition)} in a row</span>
            </div>
            <div className="stat-row">
              <span>Players Today:</span>
              <span>{globalStats.playersToday || 0}</span>
            </div>
          </div>

          <div className="stat-card">
            <h3>📈 Activity</h3>
            <div className="stat-row">
              <span>Last Updated:</span>
              <span>{formatDate(globalStats.lastUpdated)}</span>
            </div>
            <div className="stat-row">
              <span>Active Since:</span>
              <span>Launch</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLeaderboard = () => {
    if (leaderboard.length === 0) {
      return <div className="no-data">No players on leaderboard yet. Play 5+ games to be eligible!</div>;
    }

    return (
      <div className="leaderboard-content">
        <div className="leaderboard-table">
          <div className="table-header">
            <div className="rank-col">Rank</div>
            <div className="name-col">Player</div>
            <div className="rating-col">Rating</div>
            <div className="games-col">Games</div>
            <div className="winrate-col">Win Rate</div>
            <div className="streak-col">Streak</div>
            <div className="achievements-col">🏆</div>
          </div>
          
          {leaderboard.map((player) => (
            <div key={player.rank} className={`table-row ${player.rank <= 3 ? 'top-player' : ''}`}>
              <div className="rank-col">
                {player.rank <= 3 ? (
                  <span className={`medal rank-${player.rank}`}>
                    {player.rank === 1 ? '🥇' : player.rank === 2 ? '🥈' : '🥉'}
                  </span>
                ) : (
                  <span className="rank-number">#{player.rank}</span>
                )}
              </div>
              <div className="name-col">{player.playerName}</div>
              <div className="rating-col">{player.rating}</div>
              <div className="games-col">{player.gamesPlayed}</div>
              <div className="winrate-col">{player.winRate}%</div>
              <div className="streak-col">{player.currentWinStreak}</div>
              <div className="achievements-col">{player.achievements}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPlayerStats = () => {
    return (
      <div className="player-stats-content">
        <div className="player-search">
          <h3>🔍 Player Statistics</h3>
          <div className="search-form">
            <input
              type="text"
              placeholder="Enter player name..."
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchPlayerStats(playerName)}
            />
            <button onClick={() => fetchPlayerStats(playerName)}>
              Search
            </button>
          </div>
        </div>

        {playerStats && (
          <div className="player-details">
            <div className="player-header">
              <h2>📊 {playerStats.playerName}</h2>
              <div className="player-rating">
                Rating: <span className="rating-value">{playerStats.rating}</span>
              </div>
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <h3>🎮 Game Record</h3>
                <div className="stat-row">
                  <span>Games Played:</span>
                  <span>{playerStats.gamesPlayed}</span>
                </div>
                <div className="stat-row">
                  <span>Wins:</span>
                  <span className="wins">{playerStats.gamesWon}</span>
                </div>
                <div className="stat-row">
                  <span>Losses:</span>
                  <span className="losses">{playerStats.gamesLost}</span>
                </div>
                <div className="stat-row">
                  <span>Draws:</span>
                  <span>{playerStats.gamesDrawn}</span>
                </div>
                <div className="stat-row">
                  <span>Win Rate:</span>
                  <span className="win-rate">{playerStats.winRate.toFixed(1)}%</span>
                </div>
              </div>

              <div className="stat-card">
                <h3>🔥 Streaks & Records</h3>
                <div className="stat-row">
                  <span>Current Win Streak:</span>
                  <span className="streak">{playerStats.currentWinStreak}</span>
                </div>
                <div className="stat-row">
                  <span>Longest Win Streak:</span>
                  <span>{playerStats.longestWinStreak}</span>
                </div>
                <div className="stat-row">
                  <span>Total Moves:</span>
                  <span>{playerStats.totalMoves?.toLocaleString()}</span>
                </div>
                <div className="stat-row">
                  <span>Total Play Time:</span>
                  <span>{formatDuration(playerStats.totalPlayTime)}</span>
                </div>
              </div>

              <div className="stat-card">
                <h3>⚙️ Preferences</h3>
                <div className="stat-row">
                  <span>Favorite Board Size:</span>
                  <span>{getMostPopular(playerStats.favoriteSettings?.boardSize)}×{getMostPopular(playerStats.favoriteSettings?.boardSize)}</span>
                </div>
                <div className="stat-row">
                  <span>Favorite Win Condition:</span>
                  <span>{getMostPopular(playerStats.favoriteSettings?.winCondition)}</span>
                </div>
                <div className="stat-row">
                  <span>Favorite Time Control:</span>
                  <span>{getMostPopular(playerStats.favoriteSettings?.timeControl)}</span>
                </div>
              </div>

              <div className="stat-card">
                <h3>📅 Activity</h3>
                <div className="stat-row">
                  <span>First Game:</span>
                  <span>{formatDate(playerStats.firstGameDate)}</span>
                </div>
                <div className="stat-row">
                  <span>Last Game:</span>
                  <span>{formatDate(playerStats.lastGameDate)}</span>
                </div>
                <div className="stat-row">
                  <span>Average Game Duration:</span>
                  <span>{formatDuration(playerStats.averageGameDuration)}</span>
                </div>
              </div>
            </div>

            {playerStats.achievements && playerStats.achievements.length > 0 && (
              <div className="achievements-section">
                <h3>🏆 Achievements ({playerStats.achievements.length})</h3>
                <div className="achievements-grid">
                  {playerStats.achievements.map((achievement, index) => (
                    <div key={index} className="achievement">
                      <div className="achievement-name">{achievement.name}</div>
                      <div className="achievement-desc">{achievement.description}</div>
                      <div className="achievement-date">{formatDate(achievement.earnedAt)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {playerStats.ratingHistory && playerStats.ratingHistory.length > 0 && (
              <div className="rating-history">
                <h3>📈 Recent Rating Changes</h3>
                <div className="rating-list">
                  {playerStats.ratingHistory.slice(-10).reverse().map((entry, index) => (
                    <div key={index} className="rating-entry">
                      <span className="rating-change">
                        {entry.oldRating} → {entry.newRating}
                        <span className={`change ${entry.newRating > entry.oldRating ? 'positive' : 'negative'}`}>
                          ({entry.newRating > entry.oldRating ? '+' : ''}{entry.newRating - entry.oldRating})
                        </span>
                      </span>
                      <span className="opponent">vs {entry.opponent}</span>
                      <span className={`result ${entry.result}`}>{entry.result}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="stats-leaderboard">
      <div className="stats-header">
        <div className="header-content">
          <div className="header-left">
            <h1>📊 Statistics & Leaderboard</h1>
            <div className="tab-navigation">
              <button 
                className={`tab ${activeTab === 'global' ? 'active' : ''}`}
                onClick={() => setActiveTab('global')}
              >
                Global Stats
              </button>
              <button 
                className={`tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('leaderboard')}
              >
                Leaderboard
              </button>
              <button 
                className={`tab ${activeTab === 'player' ? 'active' : ''}`}
                onClick={() => setActiveTab('player')}
              >
                Player Stats
              </button>
            </div>
          </div>
          <button className="back-button" onClick={() => onNavigate('home')}>
            ← Back to Home
          </button>
        </div>
      </div>

      <div className="stats-body">
        {loading && (
          <div className="loading-container">
            <div className="loading"></div>
            <p>Loading statistics...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error">{error}</p>
            <button onClick={() => {
              if (activeTab === 'global') fetchGlobalStats();
              else if (activeTab === 'leaderboard') fetchLeaderboard();
            }}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {activeTab === 'global' && renderGlobalStats()}
            {activeTab === 'leaderboard' && renderLeaderboard()}
            {activeTab === 'player' && renderPlayerStats()}
          </>
        )}
      </div>

      <style jsx>{`
        .stats-leaderboard {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .stats-header {
          background: rgba(0, 0, 0, 0.2);
          padding: 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-left h1 {
          margin: 0 0 15px 0;
          font-size: 24px;
        }

        .tab-navigation {
          display: flex;
          gap: 10px;
        }

        .tab {
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 5px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tab:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        .tab.active {
          background: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.4);
        }

        .back-button {
          padding: 10px 20px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 5px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .back-button:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        .stats-body {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .stat-card h3 {
          margin: 0 0 15px 0;
          font-size: 16px;
          opacity: 0.9;
        }

        .stat-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          padding: 5px 0;
        }

        .stat-row:last-child {
          margin-bottom: 0;
        }

        .leaderboard-table {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          overflow: hidden;
        }

        .table-header {
          display: grid;
          grid-template-columns: 60px 1fr 80px 70px 80px 70px 50px;
          background: rgba(255, 255, 255, 0.1);
          padding: 15px;
          font-weight: bold;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .table-row {
          display: grid;
          grid-template-columns: 60px 1fr 80px 70px 80px 70px 50px;
          padding: 12px 15px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .table-row:hover {
          background: rgba(255, 255, 255, 0.05);
        }

        .top-player {
          background: rgba(255, 215, 0, 0.1);
        }

        .medal {
          font-size: 20px;
        }

        .rank-number {
          opacity: 0.7;
        }

        .player-search {
          margin-bottom: 30px;
        }

        .search-form {
          display: flex;
          gap: 10px;
          margin-top: 10px;
        }

        .search-form input {
          flex: 1;
          padding: 10px;
          border-radius: 5px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .search-form input::placeholder {
          color: rgba(255, 255, 255, 0.6);
        }

        .search-form button {
          padding: 10px 20px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 5px;
          color: white;
          cursor: pointer;
        }

        .player-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 20px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }

        .rating-value {
          font-size: 24px;
          font-weight: bold;
          color: #FFD700;
        }

        .achievements-section {
          margin-top: 30px;
        }

        .achievements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
        }

        .achievement {
          background: rgba(255, 215, 0, 0.1);
          border: 1px solid rgba(255, 215, 0, 0.3);
          border-radius: 8px;
          padding: 15px;
        }

        .achievement-name {
          font-weight: bold;
          margin-bottom: 5px;
        }

        .achievement-desc {
          font-size: 14px;
          opacity: 0.8;
          margin-bottom: 5px;
        }

        .achievement-date {
          font-size: 12px;
          opacity: 0.6;
        }

        .rating-history {
          margin-top: 30px;
        }

        .rating-list {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding: 15px;
        }

        .rating-entry {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .rating-entry:last-child {
          border-bottom: none;
        }

        .change.positive {
          color: #4ade80;
        }

        .change.negative {
          color: #f87171;
        }

        .result.win {
          color: #4ade80;
          font-weight: bold;
        }

        .result.loss {
          color: #f87171;
        }

        .loading-container {
          text-align: center;
          padding: 50px;
        }

        .loading {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(255, 255, 255, 0.1);
          border-left: 4px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-container {
          text-align: center;
          padding: 50px;
        }

        .error {
          color: #f87171;
          margin-bottom: 20px;
        }

        .no-data {
          text-align: center;
          padding: 50px;
          opacity: 0.7;
        }

        .wins {
          color: #4ade80;
          font-weight: bold;
        }

        .losses {
          color: #f87171;
          font-weight: bold;
        }

        .win-rate {
          color: #60a5fa;
          font-weight: bold;
        }

        .streak {
          color: #fbbf24;
          font-weight: bold;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 15px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .table-header,
          .table-row {
            grid-template-columns: 50px 1fr 60px 50px 60px 50px 40px;
            font-size: 14px;
          }

          .search-form {
            flex-direction: column;
          }

          .player-header {
            flex-direction: column;
            gap: 10px;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default StatsLeaderboard;
